import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { CircleUserIcon, SearchIcon } from "./components";
import { Sidebar } from "@/components/Sidebar";
import { EditableTripNameAndDescription } from "@/components/EditableTripNameAndDescription";
import { signOut } from 'firebase/auth';
import { appFirebaseAuth } from '@/context';
import { useTrips } from '../../context/TripsContext';
import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';

export const DashboardLayout = () => {
  const { tripId } = useParams(); // This will now be the short_id from the URL
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTrip, fetchTripById, getLastTripId } = useTrips();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Handle undefined tripId
    if (!tripId || tripId === 'undefined') {
      const lastTripId = getLastTripId();
      if (lastTripId) {
        // Redirect to the last trip, preserving the current path
        const currentPath = location.pathname.split('/').slice(3).join('/'); // Get path after /trip/undefined/
        navigate(`/trip/${lastTripId}${currentPath ? '/' + currentPath : ''}`, { replace: true });
      } else {
        // No last trip, redirect to trips selection
        navigate('/trips', { replace: true });
      }
      return;
    }

    fetchTripById(tripId);
  }, [tripId, fetchTripById, getLastTripId, navigate, location.pathname]);

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Use short_id for navigation if available, fallback to tripId from URL
  const navId = currentTrip?.short_id || tripId;


  const handleLogoutClick = async () => {
    signOut(appFirebaseAuth);
    navigate('/login');
  }

  const onSettingsClick = async () => {
    navigate(`/trip/${navId}/settings`);
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  }
  
  return (
    <div className="flex min-h-screen w-full overflow-x-hidden">
      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden md:block flex-shrink-0 fixed left-0 top-0 bottom-0 z-30">
        <Sidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden animate-in fade-in duration-200"
            onClick={closeMobileMenu}
          />
          {/* Sidebar */}
          <div className="fixed left-0 top-0 bottom-0 w-3/4 max-w-sm z-50 md:hidden animate-in slide-in-from-left duration-300">
            <Sidebar />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex flex-1 flex-col min-w-0 md:ml-64">
        {/* Top Header */}
        <header className="sticky top-0 flex h-16 items-center gap-2 md:gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-3 md:px-6 z-50">
          <div className="flex w-full items-center gap-2 md:gap-4 min-w-0">
            {/* Hamburger Menu - Only on mobile */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden flex-shrink-0"
              onClick={toggleMobileMenu}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
              <span className="sr-only">Toggle menu</span>
            </Button>

            <div className="flex-shrink-0 min-w-0 max-w-[50%] md:max-w-none">
              <EditableTripNameAndDescription />
            </div>
            <form className="ml-auto flex-1 sm:flex-initial min-w-0">
              <div className="relative">
                <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search..."
                  className="pl-8 w-full sm:w-[200px] lg:w-[300px]"
                />
              </div>
            </form>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="icon" className="rounded-full flex-shrink-0">
                  <CircleUserIcon className="h-5 w-5" />
                  <span className="sr-only">Toggle user menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onSettingsClick}>Settings</DropdownMenuItem>
                <DropdownMenuItem>Support</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogoutClick}>Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex flex-1 flex-col gap-4 p-6 md:gap-8 md:p-6 lg:p-8 overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-[1400px] mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
